import React, { useState } from 'react';
import client from '../../api/client';
import { emptyBabyFormData } from '../../utils/data/FormData';
import LoadingSpinner from '../utils/LoadingSpinner';
import { SUBMIT_ABDL_FORM_API } from '../../utils/ApiRoutes';

function BabyForm() {
  const submitStates = ['waiting', 'inprogress', 'failed', 'success'];
  const [formData, setFormData] = useState(emptyBabyFormData);
  const [submitState, setSubmitState] = useState(submitStates[0]);
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    setSubmitState(submitStates[0]);
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const validateForm = () => {
    const newErrors = {};
    [
      'firstName',
      'lastName',
      'email',
      'agePlayLevel',
      'diaperUse',
      'caregiverNeeds',
      'experience',
      'message',
    ].forEach((field) => {
      if (!formData[field]) newErrors[field] = 'This field is required';
    });
    if (formData.haveWeMet === 'yes' && !formData.whenWhere) {
      newErrors.whenWhere = 'Please specify when and where you met.';
    }
    return newErrors;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitState(submitStates[1]);
    const validationErrors = validateForm();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
    } else {
      client
        .post(SUBMIT_ABDL_FORM_API, formData, false)
        .then(() => {
          setSubmitState(submitStates[3])
          setFormData(emptyBabyFormData);
          setErrors({});
        }) 
        .catch(() => setSubmitState(submitStates[2]));
    }
  };

  return (
    <form onSubmit={handleSubmit} className='grid gap-y-4 lg:gap-y-6'>
      <section className='grid gap-y-4 lg:gap-y-6 lg:grid-cols-2 gap-2'>
        {/* Name Fields */}
        {['firstName', 'lastName', 'email'].map((field) => (
          <div key={field}>
            <label
              className='block text-sm font-bold text-gray-700'
              htmlFor={field}
            >
              {field === 'email'
                ? 'Email'
                : field.replace(/^\w/, (c) => c.toUpperCase())}{' '}
              <span className='text-error-red'>*</span>
            </label>
            <input
              type={field === 'email' ? 'email' : 'text'}
              id={field}
              name={field}
              value={formData[field]}
              onChange={handleChange}
              className={`shadow border rounded bg-gray-100 w-full py-2 px-3 ${
                errors[field] ? 'border-error-red' : ''
              }`}
              required
            />
            {errors[field] && (
              <p className='text-error-red text-xs italic'>{errors[field]}</p>
            )}
          </div>
        ))}
      </section>

      {/* Optional Info */}
      <section className='grid gap-y-4 lg:gap-y-6 lg:grid-cols-2 gap-4'>
        {['phoneNumber', 'location'].map((field) => (
          <div key={field}>
            <label
              className='block text-sm font-bold text-gray-700'
              htmlFor={field}
            >
              {field === 'phoneNumber' ? 'Phone Number' : 'Location'} (optional)
            </label>
            <input
              type='text'
              id={field}
              name={field}
              value={formData[field]}
              onChange={handleChange}
              className='shadow border rounded w-full bg-gray-100 py-2 px-3'
            />
          </div>
        ))}
      </section>

      {/* Adult Baby Specific Questions */}
      <section className='space-y-4'>
        <div>
          <label
            htmlFor='agePlayLevel'
            className='block text-sm font-bold text-gray-700'
          >
            Age Play Level <span className='text-error-red'>*</span>
          </label>
          <select
            name='agePlayLevel'
            id='agePlayLevel'
            value={formData.agePlayLevel}
            onChange={handleChange}
            className={`shadow border rounded bg-gray-100 w-full py-2 px-3 ${
              errors.agePlayLevel ? 'border-error-red' : ''
            }`}
          >
            <option value=''>Select one...</option>
            <option value='infant'>Infant</option>
            <option value='toddler'>Toddler</option>
            <option value='preschooler'>Preschooler</option>
            <option value='little'>Little (5+)</option>
          </select>
          {errors.agePlayLevel && (
            <p className='text-error-red text-xs italic'>
              {errors.agePlayLevel}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor='diaperUse'
            className='block text-sm font-bold text-gray-700'
          >
            Nappy Diaper Use <span className='text-error-red'>*</span>
          </label>
          <select
            name='diaperUse'
            id='diaperUse'
            value={formData.diaperUse}
            onChange={handleChange}
            className={`shadow border rounded bg-gray-100 w-full py-2 px-3 ${
              errors.diaperUse ? 'border-error-red' : ''
            }`}
          >
            <option value=''>Select one...</option>
            <option value='yes'>Yes</option>
            <option value='sometimes'>Sometimes</option>
            <option value='no'>No</option>
          </select>
          {errors.diaperUse && (
            <p className='text-error-red text-xs italic'>{errors.diaperUse}</p>
          )}
        </div>

        <div>
          <label
            htmlFor='caregiverNeeds'
            className='block text-sm font-bold text-gray-700'
          >
            What type of caregiver are you looking for?{' '}
            <span className='text-error-red'>*</span>
          </label>
          <input
            type='text'
            id='caregiverNeeds'
            name='caregiverNeeds'
            value={formData.caregiverNeeds}
            onChange={handleChange}
            className={`shadow border rounded bg-gray-100 w-full py-2 px-3 ${
              errors.caregiverNeeds ? 'border-error-red' : ''
            }`}
          />
          {errors.caregiverNeeds && (
            <p className='text-error-red text-xs italic'>
              {errors.caregiverNeeds}
            </p>
          )}
        </div>

        <div>
          <label
            htmlFor='experience'
            className='block text-sm font-bold text-gray-700'
          >
            Describe your experience as an adult baby{' '}
            <span className='text-error-red'>*</span>
          </label>
          <textarea
            id='experience'
            name='experience'
            rows={4}
            value={formData.experience}
            onChange={handleChange}
            className={`shadow border bg-gray-100 rounded w-full py-2 px-3 ${
              errors.experience ? 'border-error-red' : ''
            }`}
          />
          {errors.experience && (
            <p className='text-error-red text-xs italic'>{errors.experience}</p>
          )}
        </div>

        <div>
          <label
            htmlFor='fetishes'
            className='block text-sm font-bold text-gray-700'
          >
            Optional: Fetishes or Kinks
          </label>
          <textarea
            id='fetishes'
            name='fetishes'
            rows={3}
            value={formData.fetishes}
            onChange={handleChange}
            className='shadow border bg-gray-100 rounded w-full py-2 px-3'
          />
        </div>

        <div>
          <label
            htmlFor='limits'
            className='block text-sm font-bold text-gray-700'
          >
            Optional: Hard Limits / Boundaries
          </label>
          <textarea
            id='limits'
            name='limits'
            rows={3}
            value={formData.limits}
            onChange={handleChange}
            className='shadow border bg-gray-100 rounded w-full py-2 px-3'
          />
        </div>

        {/* New Fields: Have We Met + When/Where */}
        <div>
          <label
            htmlFor='haveWeMet'
            className='block text-sm font-bold text-gray-700'
          >
            Have we met before or had a session before?
          </label>
          <select
            name='haveWeMet'
            id='haveWeMet'
            value={formData.haveWeMet}
            onChange={handleChange}
            className='shadow border bg-gray-100 rounded w-full py-2 px-3'
          >
            <option value=''>Select an option</option>
            <option value='yes'>Yes</option>
            <option value='no'>No</option>
          </select>
        </div>

        <div>
          <label
            htmlFor='whenWhere'
            className='block text-sm font-bold text-gray-700'
          >
            If so, when and where?
          </label>
          <input
            type='text'
            id='whenWhere'
            name='whenWhere'
            value={formData.whenWhere}
            onChange={handleChange}
            className={`shadow border bg-gray-100 rounded w-full py-2 px-3 ${
              errors.whenWhere ? 'border-error-red' : ''
            }`}
            placeholder='E.g., March 2024 in Manchester'
          />
          {errors.whenWhere && (
            <p className='text-error-red text-xs italic'>{errors.whenWhere}</p>
          )}
        </div>

        <div>
          <label
            htmlFor='message'
            className='block text-sm font-bold text-gray-700'
          >
            Anything else we should know?{' '}
            <span className='text-error-red'>*</span>
          </label>
          <textarea
            id='message'
            name='message'
            rows={4}
            value={formData.message}
            onChange={handleChange}
            className={`shadow border bg-gray-100 rounded w-full py-2 px-3 ${
              errors.message ? 'border-error-red' : ''
            }`}
          />
          {errors.message && (
            <p className='text-error-red text-xs italic'>{errors.message}</p>
          )}
        </div>
      </section>

      {/* Submit Button */}
      <div className='grid items-center'>
        <button
          type='submit'
          className='w-full bg-colour6 hover:brightness-95 uppercase text-white py-2 rounded transition'
          aria-label='Submit submissive application'
          disabled={submitState === submitStates[1]}
        >
          {submitState === submitStates[1] ? (
            <div className='flex justify-center mx-auto'>
              <LoadingSpinner sm={true} />
            </div>
          ) : (
            <span>Make Contact</span>
          )}
        </button>
      </div>

      {/* Status Messages */}
      {submitState === submitStates[2] && (
        <div className='text-error-red mt-3 text-center'>
          Unable to submit the form. Please try again.
        </div>
      )}
      {submitState === submitStates[3] && (
        <div className='text-green-500 mt-3 text-center'>
          Application submitted successfully!
        </div>
      )}
    </form>
  );
}

export default BabyForm;
